import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import styles from './ClinicPatientForm.module.scss';
import DashboardTableCard from '../components/DashboardTableCard';
import { Row, Col } from 'react-bootstrap';
import Loader from '../components/Loader';
import ModalForm from '../components/ModalForm';
import AddPatientResponse from '../components/AddPatientResponse';
import SubmitButton from '../components/SubmitButton';
import PatientHeader from '../components/PatientHeader';
import ModalCopyLink from '../components/ModalCopyLink';
import { BsTrash } from 'react-icons/bs';
import { alertModal, confirmModal } from '../utils/modalController';
import Table from '../components/Table';
import Button from '../components/Button';
import { fetchAuth } from '../utils/fetchAuth';

const apiUrl = process.env.REACT_APP_API_URL || '';
const TrashIcon = BsTrash as React.FC<{ size?: number; color?: string }>;

const ClinicPatientForm: React.FC = () => {

    const [forms, setForms] = useState<FormResponse[]>([]);
    const [user, setUser] = useState<User | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [showModalInfo, setShowModalInfo] = useState(false);
    const [showModalSelectForm, setShowModalSelectForm] = useState(false);
    const [age, setAge] = useState<number | null>(null);
    const [selectedForm, setSelectedForm] = useState<FormResponse | null>(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    const { id } = useParams<{ id: string }>();

    const navigate = useNavigate();

    useEffect(() => {
        Promise.all([
            fetchAuth(apiUrl + `/consultorio/patients/${id}/form-responses`),
            fetchAuth(`${apiUrl}/consultorio/patients/${id}`),
        ])
            .then(async ([formsRes, patientRes]) => {
                if (!formsRes.ok || !patientRes.ok) {
                    throw new Error('Erro ao buscar dados');
                }

                const formsJson = await formsRes.json();
                const patientJson = await patientRes.json();

                if (!patientJson.user){
                    throw new Error("Usuário não encontrado");
                }

                const userRes = await fetchAuth(`${apiUrl}/consultorio/users/${patientJson.user.id}`);
                if (!userRes.ok) {
                    const error_json = await userRes.json();
                    throw new Error(error_json.message || "Erro desconhecido");
                }

                const userJson = await userRes.json();

                setForms(formsJson);
                setUser(userJson);
                setAge(userJson?.birthday ? Math.floor((new Date().getTime() - new Date(userJson.birthday).getTime()) / (1000 * 60 * 60 * 24 * 365)) : null);
            })
            .catch((err) => {
                console.error('Erro ao buscar dados:', err);
                alertModal('Erro ao buscar dados: ' + err);
                setForms([]);
                setUser(null);
                setAge(null);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [id, showModal]);

    if (loading) return <Loader />;

    return (
        <>
            <Container className={styles.container}>
                <h2 className={styles.title}>Consultório</h2>
                {loading ? (
                    <Loader />
                ) : (
                    <>
                        <Row className={styles["row-card"]}>
                            <Col>
                                <PatientHeader
                                    user={user!}
                                    age={age}
                                    breadcrumbItems={[
                                        { label: "Consultório", href: "/consultorio" },
                                        { label: "Pacientes", href: "/consultorio/pacientes" },
                                        { label: user?.name || "Paciente", href: `/consultorio/pacientes/${id}` },
                                        { label: "Formulários" },
                                    ]}
                                />
                            </Col>
                        </Row>
                    </>
                )}
            </Container>
            <DashboardTableCard
                welcomeMessage={`Formulários`}
                tableData={{
                    headings: ['Título', 'Respondido em', 'Ações'],
                    data: [
                        [<a href='#' className={styles["link"]} onClick={(e) => {
                            e.preventDefault();
                            setShowModal(true);
                            setSelectedForm(null);
                        }}>Novo Formulário</a>],
                        ...forms.map((form) => [
                            <a href={form.answered_at ? '#' : '#'} className={styles["link"]} onClick={(e) => {
                                e.preventDefault();
                                setSelectedForm(form);
                                if (form.answered_at) {
                                    if (!form.multiple) {
                                        navigate(`/consultorio/pacientes/${id}/formularios/${form.id}`);
                                    } else {
                                        setShowModalSelectForm(true);
                                    }
                                }
                                else {
                                    setShowModalInfo(true);
                                }
                            }}>
                                {form.title}
                            </a>,
                            form.answered_at ? new Date(form.answered_at).toLocaleDateString('pt-BR', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                            }) : '---',
                            <a href='#' className={styles["link"]} onClick={async (e) => {
                                e.preventDefault();
                                if (!await confirmModal('Tem certeza que deseja excluir esta resposta? Esta ação não pode ser desfeita.')) return;

                                fetchAuth(`${apiUrl}/consultorio/form-responses/${form.id}`, {
                                    method: 'DELETE',
                                })
                                    .then(async (res) => {
                                        if (!res.ok) {
                                            const error_json = await res.json();
                                            throw new Error(error_json.message || "Erro desconhecido");
                                        }
                                        return res.json();
                                    })
                                    .then((json) => {
                                        setForms(forms.filter(f => f.id !== form.id));
                                    })
                                    .catch((err) => {
                                        console.error('Erro ao excluir formulário:', err);
                                        alertModal('Erro ao excluir formulário: ' + err);
                                    });
                            }}>
                                <TrashIcon />
                            </a>
                        ])
                    ]

                }}
            />
            <ModalForm
                title={selectedForm?.title ?? 'Selecione a resposta do formulário'}
                show={showModalSelectForm}
                onHide={() => { setShowModalSelectForm(false) }}
                onSubmit={(e) => {
                    e.preventDefault();
                    setShowModalSelectForm(false);
                }}
                footerButtons={[
                    <Button
                        className={styles["btn-modal"]}
                        label='Fechar'
                        onClick={() => {
                            setShowModalSelectForm(false);
                            setSelectedForm(null);
                        }}
                    />
                ]}
            >
                <Table
                    headings={['Respondido em']}
                    data={selectedForm?.responses.map((response) => [
                        <Link
                            to={`/consultorio/pacientes/${id}/formularios/${selectedForm?.id}?response=${response.id}`}
                            className={styles["link"]}
                            key={response.id}
                        >
                            {new Date(response.answered_at).toLocaleDateString('pt-BR', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                            })}
                        </Link>
                    ]) || []}
                />
            </ModalForm >
            <ModalForm
                title="Novo Formulário"
                show={showModal}
                onHide={() => { setShowModal(false) }}
                onSubmit={(e) => {
                    e.preventDefault();
                    setSaving(true);
                    const formData = new FormData(e.currentTarget);
                    const showConfigFields = formData.get('show_config_fields') === 'on' ? 1 : 0;
                    const multipleResponses = formData.get('multiple_responses') === 'on' ? 1 : 0;

                    // Se quiser garantir que os campos estão no FormData corretamente:
                    formData.set('show_config_fields', showConfigFields.toString());
                    formData.set('multiple_responses', multipleResponses.toString());
                    formData.set('patient_id', id || '');

                    const formDataJson = Object.fromEntries(formData.entries());

                    fetchAuth(`${apiUrl}/consultorio/form-responses${formDataJson.id !== '' ? `/${formDataJson.id}` : ''}`, {
                        method: formDataJson.id !== '' ? 'PUT' : 'POST',
                        body: JSON.stringify(formDataJson), // aqui vai o formDataJson
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    })
                        .then(async (res) => {
                            if (!res.ok) {
                                const error_json = await res.json();
                                throw new Error(error_json.message || "Erro desconhecido");
                            }
                            return res.json();
                        })
                        .then((json) => {
                            setShowModal(false);
                            setSelectedForm({
                                id: json.id,
                                title: json.title,
                                multiple: json.multiple,
                                responses: json.responses || [],
                                answered_at: json.answered_at ?? '---',
                            })
                            setShowModalInfo(true);
                        })
                        .catch((err) => {
                            console.error('Erro ao enviar formulário:', err);
                            alertModal('Erro ao enviar formulário: ' + err);
                        })
                        .finally(() => {
                            setSaving(false);
                        });

                }}
                footerButtons={[
                    <SubmitButton
                        className={styles["btn-modal"]}
                        disabled={saving}
                    >
                        {saving ? 'Salvando...' : 'Salvar'}
                    </SubmitButton>,
                ]}
            >
                <AddPatientResponse formId={selectedForm?.id} />
            </ModalForm >
            <ModalCopyLink
                show={showModalInfo}
                onClose={() => setShowModalInfo(false)}
                urlPath={`formularios/${selectedForm?.id}`}
            />
        </>
    );
};

export default ClinicPatientForm;
